import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Module-level dedup: prevents same session_id being exchanged twice across remounts
// (e.g. React StrictMode dev double-mount, or any deps change re-running the effect).
const consumedSessionIds = new Set();
let inflightPromise = null;

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function AuthCallback() {
  const hasProcessed = useRef(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash;
    const sessionId = new URLSearchParams(hash.replace('#', '?')).get('session_id');

    // Clear the hash immediately so any unexpected remount of this component
    // (e.g. StrictMode dev re-mount, or back/forward navigation) cannot re-trigger
    // the exchange with an already-consumed session_id.
    if (hash) {
      try {
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
      } catch {}
    }

    if (!sessionId) {
      navigate('/login', { replace: true });
      return;
    }

    if (consumedSessionIds.has(sessionId)) {
      // Already used this session_id — let the existing flow finish; do nothing here.
      return;
    }
    consumedSessionIds.add(sessionId);

    const exchangeSession = async () => {
      try {
        // De-dupe concurrent invocations across remounts at the module level
        if (!inflightPromise) {
          inflightPromise = fetch(`${API}/auth/session`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ session_id: sessionId }),
          });
        }
        const res = await inflightPromise;
        inflightPromise = null;

        if (res.ok) {
          const userData = await res.json();
          login(userData);
          const STAFF = ['super_admin', 'admin', 'deal_manager', 'content_manager', 'investor_relations', 'view_only'];
          if (STAFF.includes(userData.role)) {
            navigate('/admin', { state: { user: userData }, replace: true });
          } else if (userData.status === 'pending' || userData.status === 'rejected') {
            navigate('/pending', { replace: true });
          } else {
            navigate('/portal', { state: { user: userData }, replace: true });
          }
        } else {
          navigate('/login', { replace: true });
        }
      } catch {
        inflightPromise = null;
        navigate('/login', { replace: true });
      }
    };
    exchangeSession();
    // Intentionally empty deps: this effect must run exactly once per mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-[#1A1A1A] flex items-center justify-center">
      <div className="text-center">
        <div className="w-8 h-8 border-2 border-[#A0743A] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-[#C9A96E] text-sm uppercase tracking-[0.2em]">Authenticating...</p>
      </div>
    </div>
  );
}
